import React, { useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  ChevronDownIcon,
  CheckIcon,
  RocketLaunchIcon,
  BuildingOfficeIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { MasterPlanTask } from "../../services/masterPlan";
import TaskModal from "../TaskModal";
import TaskDetailPanel from "../TaskDetailPanel";
import { formatTaskStatus } from "../../utils/enumFormatting";

interface TaskListViewProps {
  tasks: MasterPlanTask[];
  entityType?: "company" | "segment" | "proposition" | "position" | "okr";
  entityId?: string;
  canEdit: boolean;
  organizationId: string;
  positions?: any[];
  segments?: any[];
  propositions?: any[];
  groups?: any[];
  companies?: any[];
  onTaskUpdate: (taskId: string, updates: Partial<MasterPlanTask>) => void;
  onTaskCreate: (task: MasterPlanTask) => void;
  onTaskDelete: (taskId: string) => void;
  hideCreateButton?: boolean;
}

const TaskListView: React.FC<TaskListViewProps> = ({
  tasks,
  entityType,
  entityId,
  canEdit,
  organizationId,
  positions = [],
  segments = [],
  propositions = [],
  groups = [],
  companies = [],
  onTaskUpdate,
  onTaskCreate,
  onTaskDelete,
  hideCreateButton = false,
}) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MasterPlanTask | null>(null);

  // Backend now handles entity filtering, so we just use tasks directly
  const filteredTasks = tasks;

  // Group tasks by area for list view
  const tasksByGroup = React.useMemo(() => {
    const grouped: Record<string, MasterPlanTask[]> = {};

    filteredTasks.forEach((task) => {
      let groupKey = "Unassigned";

      if (task.area_name) {
        groupKey = task.area_name;
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(task);
    });

    // Sort groups alphabetically, with Unassigned last
    const sortedGroups: Record<string, MasterPlanTask[]> = {};
    const keys = Object.keys(grouped).sort((a, b) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });

    keys.forEach((key) => {
      sortedGroups[key] = grouped[key];
    });

    return sortedGroups;
  }, [filteredTasks]);

  const handleViewTask = (task: MasterPlanTask) => {
    setSelectedTask(task);
    setShowDetailPanel(true);
  };

  const handleEditTask = (task: MasterPlanTask, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setSelectedTask(task);
    setShowDetailPanel(true);
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    // Convert Title Case from select to snake_case for backend
    const statusMap: Record<string, string> = {
      "Not Started": "not_started",
      "In Progress": "in_progress",
      Complete: "complete",
      Blocked: "blocked",
    };

    await onTaskUpdate(taskId, {
      status: statusMap[newStatus] as
        | "not_started"
        | "in_progress"
        | "complete"
        | "blocked",
    });
  };

  const getStatusColor = (status: string) => {
    // Format status to Title Case for comparison
    const formattedStatus = formatTaskStatus(status);
    switch (formattedStatus) {
      case "Complete":
        return "text-green-600 bg-green-50 border-green-200";
      case "In Progress":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "Blocked":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusIcon = (status: string) => {
    // Format status to Title Case for comparison
    const formattedStatus = formatTaskStatus(status);
    switch (formattedStatus) {
      case "Complete":
        return <CheckIcon className="w-4 h-4" />;
      case "In Progress":
        return <RocketLaunchIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getAssigneeName = (task: MasterPlanTask) => {
    return task.assigned_to_name || "Unassigned";
  };

  const getContextDisplay = (task: MasterPlanTask) => {
    // Check both context_object_type and context_type for backward compatibility
    const contextType = task.context_object_type || task.context_type;
    if (!contextType || !task.context_object_id) return null;

    switch (contextType) {
      case "position": {
        const position = positions?.find(
          (p) => p.id === task.context_object_id,
        );
        return position
          ? `${position.proposition_name} × ${position.segment_name}`
          : "";
      }
      case "segment": {
        const segment = segments?.find((s) => s.id === task.context_object_id);
        return segment?.name || "";
      }
      case "proposition": {
        const proposition = propositions?.find(
          (p) => p.id === task.context_object_id,
        );
        return proposition?.name || "";
      }
      case "group": {
        const group = groups?.find((g) => g.id === task.context_object_id);
        return group?.name || "";
      }
      case "company": {
        const company = companies?.find((c) => c.id === task.context_object_id);
        return company?.name || "";
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
        <h2 className="text-lg font-semibold">
          Tasks - List View
          {entityType && (
            <span className="text-sm text-gray-500 ml-2">
              ({filteredTasks.length} tasks)
            </span>
          )}
        </h2>
        {canEdit && !hideCreateButton && (
          <button
            onClick={() => setShowAddTask(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4" />
            Add Task
          </button>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          {Object.entries(tasksByGroup).map(([groupKey, groupTasks]) => (
            <div
              key={groupKey}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {groupKey === "Unassigned" ? "Unassigned Area" : groupKey}
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    ({groupTasks.length})
                  </span>
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {groupTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => handleViewTask(task)}
                    className="group hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                  >
                    <div className="px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Title and status row */}
                          <div className="flex items-center gap-3 mb-2">
                            {task.formatted_reference && (
                              <span className="text-xs font-mono text-gray-500">
                                {task.formatted_reference}
                              </span>
                            )}
                            <h4 className="font-semibold text-gray-900">
                              {task.title}
                            </h4>
                            {/* Quick status change dropdown */}
                            {canEdit ? (
                              <select
                                value={formatTaskStatus(task.status)}
                                onChange={(e) =>
                                  handleStatusChange(task.id, e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(task.status)}`}
                              >
                                <option value="Not Started">Not Started</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Complete">Complete</option>
                                <option value="Blocked">Blocked</option>
                              </select>
                            ) : (
                              <span
                                className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(task.status)}`}
                              >
                                {formatTaskStatus(task.status)}
                              </span>
                            )}
                          </div>

                          {/* Description */}
                          {task.description && (
                            <p className="text-sm text-gray-600 mb-2">
                              {task.description}
                            </p>
                          )}

                          {/* Context */}
                          {getContextDisplay(task) && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                              <Squares2X2Icon className="w-3 h-3" />
                              <span>{getContextDisplay(task)}</span>
                            </div>
                          )}

                          {/* OKR alignment */}
                          {task.okr_id && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                              <RocketLaunchIcon className="w-3 h-3" />
                              <span>
                                {task.okr_objective || "Unknown OKR"}
                              </span>
                            </div>
                          )}

                          {/* Meta row */}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {task.assigned_to_id && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="w-3 h-3" />
                                <span>
                                  {getAssigneeName(task)}
                                </span>
                              </div>
                            )}
                            {task.due_date && (
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="w-3 h-3" />
                                <span>
                                  {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {task.priority && (
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs ${
                                  task.priority === "high"
                                    ? "bg-red-100 text-red-700"
                                    : task.priority === "medium"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {task.priority.charAt(0).toUpperCase() +
                                  task.priority.slice(1)}
                              </span>
                            )}
                            {task.area_name && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-500 text-white">
                                {task.area_name}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        {canEdit && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => handleEditTask(task, e)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTaskDelete(task.id);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Task Modal - for creating new tasks */}
      {showAddTask && (
        <TaskModal
          task={null} // null = create mode
          organizationId={organizationId}
          onClose={() => {
            setShowAddTask(false);
          }}
          onSave={async (createdTask) => {
            // TaskModal handles the create API call and returns the new task
            // Notify parent to refresh the task list
            await onTaskCreate(createdTask);
            setShowAddTask(false);
          }}
          segments={segments}
          propositions={propositions}
          groups={groups}
          positions={positions}
          companies={companies}
          isAdmin={canEdit}
          initialValues={
            entityType === "okr" && entityId
              ? { okr_id: entityId } // Only pre-fill OKR alignment for OKR pages
              : entityType && entityId
                ? {
                    context_type: entityType,
                    context_object_id: entityId,
                  }
                : undefined
          }
        />
      )}

      {/* Task Detail Panel - for viewing and editing existing tasks */}
      <TaskDetailPanel
        task={selectedTask}
        isOpen={showDetailPanel}
        onClose={() => {
          setShowDetailPanel(false);
          setSelectedTask(null);
        }}
        onUpdate={(updatedTask) => {
          // Update the task in the parent component
          onTaskUpdate(updatedTask.id!, updatedTask);
          setSelectedTask(updatedTask);
        }}
        onDelete={async (taskId) => {
          await onTaskDelete(taskId);
          setShowDetailPanel(false);
          setSelectedTask(null);
        }}
        organizationId={organizationId}
        currentUserId="" // Will be fetched from AuthContext by the panel
        canEdit={canEdit}
        isAdmin={canEdit}
        segments={segments}
        propositions={propositions}
        groups={groups}
        positions={positions}
        companies={companies}
      />
    </div>
  );
};

export default TaskListView;
